import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserOrganization } from './useOrganization';
import type { Tables, TablesInsert } from '@/integrations/supabase/types';

type DNSPolicy = Tables<'dns_policies'>;
type DNSPolicyInsert = TablesInsert<'dns_policies'>;
type PolicyDomain = Tables<'policy_domains'>;

// Helper function to notify extension to sync policies
const notifyExtensionSync = () => {
  if (typeof chrome !== 'undefined' && chrome.runtime) {
    chrome.runtime.sendMessage({ type: "SYNC_POLICIES" })
      .catch(() => {
        // Ignore errors - extension might not be installed or available
      });
  }
};

// Main hook for fetching organization policies
export function useOrganizationPolicies() {
  const { data: organization } = useUserOrganization();
  
  return useQuery({
    queryKey: ['dns-policies', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      
      const { data, error } = await supabase
        .from('dns_policies')
        .select(`
          *,
          policy_domains (
            id,
            domain,
            action,
            created_at
          )
        `)
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return data;
    },
    enabled: !!organization?.id,
  });
}

// Hook for creating DNS policies
export function useCreateDNSPolicy() {
  const queryClient = useQueryClient();
  const { data: organization } = useUserOrganization();
  
  return useMutation({
    mutationFn: async (policy: DNSPolicyInsert & { custom_domains?: string; domain_action?: 'block' | 'allow' }) => {
      let orgId = organization?.id;

      // If no organization exists, create one automatically
      if (!orgId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data: newOrg, error: orgError } = await supabase
          .from('organizations')
          .insert({
            name: `${user.email}'s Organization`,
            owner_id: user.id,
            organization_type: 'personal',
            is_active: true,
          })
          .select()
          .single();

        if (orgError) throw new Error(`Failed to create organization: ${orgError.message}`);

        orgId = newOrg.id;
        queryClient.invalidateQueries({ queryKey: ['organization'] });
      }

      const { custom_domains, domain_action = 'block', ...policyData } = policy;

      // If setting this policy as default, clear any existing default first
      if (policyData.is_default) {
        await supabase
          .from('dns_policies')
          .update({ is_default: false })
          .eq('organization_id', orgId)
          .eq('is_default', true);
      }

      // Create the policy
      const { data: createdPolicy, error: policyError } = await supabase
        .from('dns_policies')
        .insert({ ...policyData, organization_id: orgId })
        .select()
        .single();

      if (policyError) throw policyError;

      // Handle custom domains if provided
      if (custom_domains && custom_domains.trim()) {
        const domains = custom_domains
          .split(/[,\n\r]+/)
          .map(d => d.trim().toLowerCase().replace(/\/$/, ''))
          .filter(d => d.length > 0);

        if (domains.length > 0) {
          const { error: domainsError } = await supabase
            .from('policy_domains')
            .insert(domains.map(d => ({ policy_id: createdPolicy.id, domain: d, action: domain_action })));

          if (domainsError) throw domainsError;
        }
      }

      return createdPolicy;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dns-policies', organization?.id] });
      
      // Notify extension to sync policies immediately
      notifyExtensionSync();
    },
  });
}

// Hook for updating DNS policies
export function useUpdateDNSPolicy() {
  const queryClient = useQueryClient();
  const { data: organization } = useUserOrganization();
  
  return useMutation({
    mutationFn: async ({
      id,
      updates
    }: {
      id: string;
      updates: Partial<DNSPolicy> & { custom_domains?: string; domain_action?: 'block' | 'allow' };
    }) => {
      const { custom_domains, domain_action = 'block', ...policyUpdates } = updates;

      // If setting this policy as default, clear any existing default first
      if (policyUpdates.is_default) {
        await supabase
          .from('dns_policies')
          .update({ is_default: false })
          .eq('organization_id', organization!.id)
          .eq('is_default', true)
          .neq('id', id);
      }

      const { data: updatedPolicy, error: policyError } = await supabase
        .from('dns_policies')
        .update(policyUpdates)
        .eq('id', id)
        .select()
        .single();

      if (policyError) throw policyError;

      if (custom_domains !== undefined) {
        await supabase.from('policy_domains').delete().eq('policy_id', id);

        if (custom_domains.trim()) {
          const domains = custom_domains
            .split(/[,\n\r]+/)
            .map(d => d.trim().toLowerCase().replace(/\/$/, ''))
            .filter(d => d.length > 0);

          if (domains.length > 0) {
            const { error: domainsError } = await supabase
              .from('policy_domains')
              .insert(domains.map(d => ({ policy_id: id, domain: d, action: domain_action })));

            if (domainsError) throw domainsError;
          }
        }
      }

      return updatedPolicy;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dns-policies', organization?.id] });
      
      // Notify extension to sync policies immediately
      notifyExtensionSync();
    },
  });
}

// Hook for deleting DNS policies
export function useDeleteDNSPolicy() {
  const queryClient = useQueryClient();
  const { data: organization } = useUserOrganization();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('dns_policies')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dns-policies', organization?.id] });
      
      // Notify extension to sync policies immediately
      notifyExtensionSync();
    },
  });
}

